import { VisuallyHidden } from "reakit";
import { Box } from "../../Box/Box";
import { Button } from "../../Button/Button";
import pxToRem from "../../utils/pxToRem";

import { useCalendarContext } from "../context";
import { MONTHS, YEARS_LIST_INTERVAL, NAVIGATION_STATES, NEXT_CALENDAR_TYPE } from "../constants";
import { parseDate, getStartYearsInterval } from "../helpers";
import type { NavigationButtonType } from "./NavigationButton";
import { NavigationButton } from "./NavigationButton";
import { shouldNeverHappen } from "../../utils/shouldNeverHappen";

export function CalendarNavigation() {
  const { calendarType, setCalendarType, currentDate, onInnerDateChange, calendarId } =
    useCalendarContext();
  const originalDate = new Date(currentDate);
  const currentMonth = originalDate.getMonth();
  const currentYear = originalDate.getFullYear();
  const startYear = getStartYearsInterval(originalDate);
  // Значения заголовков у кнопок смены типа календаря
  // Также данные значения используются для описания таблиц с датами
  const navigationValues = {
    days: `${MONTHS[currentMonth]} ${currentYear}`,
    months: (
      <>
        {currentYear}
        <VisuallyHidden>{` год`}</VisuallyHidden>
      </>
    ),
    years: `${startYear}-${startYear + YEARS_LIST_INTERVAL - 1}`,
  };

  const handleTypeChange = () => {
    const stateIndex = NAVIGATION_STATES.indexOf(calendarType);
    const nextState = NAVIGATION_STATES[stateIndex + 1];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- eslint некорректно обрабатывает
    if (nextState) setCalendarType(nextState);
  };

  const handleUpdateMonth = (arrowType: NavigationButtonType) => {
    const neighbouringMonth = arrowType === "left" ? currentMonth - 1 : currentMonth + 1;
    const newDate = parseDate(new Date(currentYear, neighbouringMonth, 1));
    onInnerDateChange({ date: newDate, updateFocus: false });
  };

  const handleUpdateYear = (arrowType: NavigationButtonType) => {
    const neighbouringYear = arrowType === "left" ? currentYear - 1 : currentYear + 1;
    const newDate = parseDate(new Date(neighbouringYear, currentMonth, 1));
    onInnerDateChange({ date: newDate, updateFocus: false });
  };

  const handleUpdateYearsList = (arrowType: NavigationButtonType) => {
    const neighbouringYear =
      arrowType === "left" ? startYear - YEARS_LIST_INTERVAL : startYear + YEARS_LIST_INTERVAL;
    const newDate = parseDate(new Date(neighbouringYear, currentMonth, 1));
    onInnerDateChange({ date: newDate, updateFocus: false });
  };

  const handleNavigationClick = (arrowType: NavigationButtonType) => {
    switch (calendarType) {
      case "days":
        handleUpdateMonth(arrowType);
        break;
      case "months":
        handleUpdateYear(arrowType);
        break;
      case "years":
        handleUpdateYearsList(arrowType);
        break;
      default:
        shouldNeverHappen(calendarType);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      width={1}
      mb={calendarType === "days" ? "sp-16" : pxToRem(28)}
    >
      <NavigationButton type="left" navigationType={calendarType} onClick={handleNavigationClick} />
      <VisuallyHidden>
        <Box as="h2" aria-live="polite" id={calendarId}>
          {navigationValues[calendarType]}
        </Box>
      </VisuallyHidden>
      <Button
        kind="borderless"
        vertSize="32"
        disabled={calendarType === "years"}
        onClick={handleTypeChange}
      >
        {navigationValues[calendarType]}
        <VisuallyHidden>. {NEXT_CALENDAR_TYPE[calendarType]}</VisuallyHidden>
      </Button>
      <NavigationButton
        type="right"
        navigationType={calendarType}
        onClick={handleNavigationClick}
      />
    </Box>
  );
}
